/**
 * Billing accounts grid — the staff account list.
 *
 * Server-backed via EntityGrid over odl-app/billing-accounts/: columns,
 * order, sizing, and the frequency enum come from the grid-spec descriptor;
 * this file supplies render overrides and the caller-owned filter bar
 * (Active, Type, Frequency, Billed — each a filter[...] param). Search
 * covers the account's customer name. A row click opens the account; the
 * descriptor's trailing "select" action opens the same destination.
 *
 * Contains:
 * - BillingAccountsGrid — EntityGrid over odl-app/billing-accounts/ (default export)
 */
import { Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { fetchCustomerTypes } from '@sophocles/api/customer';
import FilterBar from '@sophocles/components/FilterBar';
import { buildStatusQuickFilter } from '@sophocles/components/FilterBar/statusQuickFilter';
import StatusChip from '@sophocles/components/StatusChip';
import { EntityGrid } from '@sophocles/data-grids';
import GridLinkCell from '@sophocles/data-grids/components/cells/GridLinkCell';
import { useGridSessionKey } from '@sophocles/data-grids/datasource/useGridPreferenceKey';
import usePersistedState from '@sophocles/hooks/usePersistedState';

function BillingAccountsGrid() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [activeFilter, setActiveFilter] = usePersistedState(useGridSessionKey('billingAccounts', 'active'), 'active');
  const [typeFilter, setTypeFilter] = usePersistedState(useGridSessionKey('billingAccounts', 'type'), null);
  const [frequencyFilter, setFrequencyFilter] = usePersistedState(
    useGridSessionKey('billingAccounts', 'frequency'),
    null,
  );
  const [billedFilter, setBilledFilter] = usePersistedState(useGridSessionKey('billingAccounts', 'billed'), null);
  const [customerTypes, setCustomerTypes] = useState([]);

  // Pre-migration sessions may still carry the retired 'billboard'/'commercial'
  // string value (the Type quick filter now selects a CustomerType id); a
  // non-numeric stored value resolves to null rather than forwarding it as a
  // customer_type_id filter param.
  const sanitizedTypeFilter = typeof typeFilter === 'number' ? typeFilter : null;

  useEffect(() => {
    fetchCustomerTypes({ limit: 200 })
      .then(response => setCustomerTypes(response?.results ?? response ?? []))
      .catch(() => setCustomerTypes([]));
  }, []);

  const extraParams = useMemo(() => {
    const params = {};
    if (activeFilter === 'active') params.is_active = true;
    else if (activeFilter === 'inactive') params.is_active = false;
    // activeFilter === 'all': no is_active filter → backend returns all
    if (sanitizedTypeFilter != null) params.customer_type_id = sanitizedTypeFilter;
    if (frequencyFilter) params.billing_frequency = frequencyFilter;
    if (billedFilter === 'billed') params.needs_billing = false;
    else if (billedFilter === 'unbilled') params.needs_billing = true;
    return params;
  }, [activeFilter, sanitizedTypeFilter, frequencyFilter, billedFilter]);

  const typeOptions = useMemo(() => customerTypes.map(type => ({ value: type.id, label: type.name })), [customerTypes]);

  const frequencyOptions = useMemo(
    () => [
      { value: 'none', label: t('billing.none', 'None') },
      { value: 'month', label: t('billing.monthly', 'Monthly') },
      { value: 'quarter', label: t('billing.quarterly', 'Quarterly') },
      { value: 'semiannual', label: t('billing.semiAnnually', 'Semi-Annually') },
      { value: 'annual', label: t('billing.annually', 'Annually') },
      { value: 'prepaid', label: t('billing.prepaid', 'Prepaid') },
      { value: 'trial', label: t('billing.trial', 'Trial') },
      { value: 'demo', label: t('billing.demo', 'Demo') },
      { value: 'internal', label: t('billing.internal', 'Internal') },
    ],
    [t],
  );

  const quickFilters = useMemo(
    () => [
      buildStatusQuickFilter({
        t,
        key: 'active',
        value: activeFilter,
        onChange: setActiveFilter,
        inactiveValue: 'inactive',
        inactiveLabel: t('general.inactive', 'Inactive'),
      }),
      {
        key: 'type',
        label: t('billing.type', 'Type'),
        type: 'single',
        allLabel: t('general.all', 'All'),
        options: typeOptions,
        value: sanitizedTypeFilter,
        onChange: value => setTypeFilter(value ?? null),
      },
      {
        key: 'frequency',
        label: t('billing.frequency', 'Frequency'),
        type: 'single',
        allLabel: t('general.all', 'All'),
        options: frequencyOptions,
        value: frequencyFilter,
        onChange: value => setFrequencyFilter(value ?? null),
      },
      {
        key: 'billed',
        label: t('billing.billed', 'Billed'),
        type: 'single',
        allLabel: t('general.all', 'All'),
        options: [
          { value: 'billed', label: t('billing.yes', 'Yes') },
          { value: 'unbilled', label: t('billing.no', 'No') },
        ],
        value: billedFilter,
        onChange: value => setBilledFilter(value ?? null),
      },
    ],
    [
      t,
      activeFilter,
      setActiveFilter,
      sanitizedTypeFilter,
      typeOptions,
      frequencyFilter,
      billedFilter,
      setTypeFilter,
      setFrequencyFilter,
      setBilledFilter,
      frequencyOptions,
    ],
  );

  const handleResetAll = useCallback(() => {
    setActiveFilter('active');
    setTypeFilter(null);
    setFrequencyFilter(null);
    setBilledFilter(null);
  }, [setActiveFilter, setTypeFilter, setFrequencyFilter, setBilledFilter]);

  const fieldOverrides = useMemo(
    () => ({
      customer_id: {
        renderCell: ({ value }) => (
          <Typography variant="body2" sx={{ fontFamily: 'Monospace' }}>
            {String(value ?? '').padStart(5, '0')}
          </Typography>
        ),
      },
      // The account name is the row's link target, same destination as the
      // trailing "select" action.
      customer_name: {
        renderCell: ({ row, value }) => (
          <GridLinkCell to={`/billing/accounts/${row.id}/`} sx={{ overflow: 'hidden' }}>
            <Typography noWrap sx={{ color: 'primary.main', fontWeight: 'var(--weight-semibold)' }}>
              {value}
            </Typography>
          </GridLinkCell>
        ),
      },
      needs_billing: {
        renderCell: ({ value }) => (
          <StatusChip color={value ? 'bad' : 'ok'} label={value ? t('billing.no', 'No') : t('billing.yes', 'Yes')} />
        ),
      },
    }),
    [t],
  );

  // The descriptor's trailing "select" action opens the account — same
  // destination as the row click.
  const actionHandlers = useMemo(
    () => ({ select: { onClick: row => navigate(`/billing/accounts/${row.id}/`), variant: 'button' } }),
    [navigate],
  );

  const toolbarContent = <FilterBar quickFilters={quickFilters} onResetAll={handleResetAll} />;

  return (
    <EntityGrid
      basePath="billing-accounts"
      fieldOverrides={fieldOverrides}
      actionHandlers={actionHandlers}
      extraParams={extraParams}
      options={{
        searchPlaceholder: t('billing.searchAccounts', 'Search accounts'),
        persistKey: 'billingAccountsGrid',
        infiniteScroll: true,
        countNoun: t('billing.accounts', 'Accounts'),
        toolbarContent,
      }}
    />
  );
}

export default BillingAccountsGrid;
